import {Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';

import {BodyTypeViewModel} from '../../../viewModels/bodyType.viewModel';
import {UserProfileViewModel} from '../../../viewModels/userProfile.viewModel';
import {UsersService} from '../../../services/users.service';
import {NotificationsService} from '../../../services/notification.service';
import {LocalisationService} from '../../../services/localisation.service';
import {DailyMealViewModel} from '../../../viewModels/dailyMeal.viewModel';
import {forkJoin} from 'rxjs';
import {DietViewModel} from '../../../viewModels/diet.viewModel';
import {AllergyViewModel} from '../../../../meals/components/viewModels/allergy.viewModel';
import {ProductsService} from '../../../services/products.service';
import {TrainingTargetViewModel} from '../../../viewModels/trainingTarget.viewModel';

@Component({
    selector: 'app-body-section',
    templateUrl: './body-section.component.html',
    styleUrls: ['./body-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BodySectionComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;

    loading = true;
    dailyMeals: DailyMealViewModel[];
    diets: DietViewModel[];

    currentlySelectedDailyMeal: DailyMealViewModel;
    currentlySelectedDiet: DietViewModel;
    currentlySelectedTrainingTarget: TrainingTargetViewModel;

    allergies: AllergyViewModel[];
    enabledByAllergy: { [key: number]: boolean; } = {};
    trainingTargets: TrainingTargetViewModel[];

    constructor(
        private readonly usersService: UsersService,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly productsService: ProductsService,
    ) {
    }

    ngOnInit() {
        forkJoin(
            this.usersService.getUserDailyMeals(),
            this.usersService.getUserDiets(),
            this.productsService.getAllergies(),
            this.usersService.getUserTrainingTargets(),
        ).subscribe(
            ([dailyMeals, diets, allergies, trainingTargets]) => {
                this.dailyMeals = dailyMeals;
                this.diets = diets;
                this.allergies = allergies;
                this.trainingTargets = trainingTargets;

                this.loading = false;

                this.changeDetectorRef.detectChanges();

                if (this.usersService.userProfile) {
                    this.initCurrentUserProfile(this.usersService.userProfile);
                }
            }
        );
    }

    updateProfile() {
        this.loading = true;

        let trainingTarget = null;

        if (this.currentlySelectedTrainingTarget) {
            trainingTarget = {id: this.currentlySelectedTrainingTarget.id};
        }

        this.usersService
            .updateUserProfile({
                daily_meals: {
                    id: this.currentlySelectedDailyMeal.id
                },
                diet: this.currentlySelectedDiet ? {id: this.currentlySelectedDiet.id} : null,
                allergies: Object.keys(this.enabledByAllergy).filter(k => this.enabledByAllergy[k]).map(k => +k).map(id => {
                    return {id};
                }),
            })
            .subscribe(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            });
    }

    get saveButtonIsDisabled(): boolean {
        return !this.currentlySelectedDailyMeal;
    }

    private initCurrentUserProfile(userProfile: UserProfileViewModel) {
        this.currentlySelectedDailyMeal = userProfile.daily_meals ? this.dailyMeals.find(dm => dm.id === userProfile.daily_meals.id) : undefined;
        this.currentlySelectedDiet = userProfile.diet ? this.diets.find(d => d.id === userProfile.diet.id) : undefined;
        this.currentlySelectedTrainingTarget = userProfile.training_target ? this.trainingTargets.find(tt => tt.id === userProfile.training_target.id) : undefined;

        this.initEnabledByAllergy(userProfile);

        this.changeDetectorRef.markForCheck();
    }


    private initEnabledByAllergy(userProfile: UserProfileViewModel) {
        this.allergies.forEach(a => this.enabledByAllergy[a.id] = false);

        userProfile.allergies.forEach(a => {
            this.enabledByAllergy[a.id] = true;
        });
    }

}
